import React, { useState, useEffect, useContext, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import NotyfContext from "../../contexts/NotyfContext";
import { useTranslation } from "react-i18next";
import { getDataVolumePerHour } from "../../redux/slices/dashboard";
import { Card } from "react-bootstrap";
import Loader from "../spinners/Loader";
import Chart from "react-apexcharts";
import { useWhitelabel } from "../../contexts/WhitelabelContext";

const DataVolumePerHourChart = ({ reload, demoMode = false }) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.user);
  const { impersonationUser } = useSelector((state) => state.impersonationUser);
  const whitelabel = useSelector((state) => state.whitelabel.whitelabel);
  const data_volume_per_hour = useSelector(
    (state) => state.dashboard.data_volume_per_hour
  );
  const [activeUser, setActiveUser] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const notyf = useContext(NotyfContext);
  const { t } = useTranslation();
  const { accentColor } = useWhitelabel();

  const isDemoOrLead = demoMode === true || activeUser.role === "lead";

  useEffect(() => {
    setActiveUser(impersonationUser.id ? impersonationUser : user);
  }, [user.id, impersonationUser.id]);

  useEffect(() => {
    setIsLoading(!isDemoOrLead);
  }, [isDemoOrLead]);

  useEffect(() => {
    const fetchDataVolumeByHour = async () => {
      setIsLoading(true);
      await dispatch(getDataVolumePerHour())
        .catch((error) => {
          notyf.open({
            type: "danger",
            message: error,
            duration: 5000,
            ripple: true,
            dismissible: false,
            position: { x: "center", y: "top" },
          });
        })
        .finally(() => {
          setTimeout(() => setIsLoading(false), 500);
        });
    };

    if (activeUser.id && demoMode === false && activeUser.role !== "lead") {
      fetchDataVolumeByHour();
    }
  }, [activeUser, reload, demoMode, dispatch, notyf]);

  const dayOfWeekNames = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];

  const hourOfDayNames = [
    "12-3 AM",
    "3-6 AM",
    "6-9 AM",
    "9-12 PM",
    "12-3 PM",
    "3-6 PM",
    "6-9 PM",
    "9-12 AM",
  ];

  const staticDemoData = [
    { hourOfDay: 1, dayOfWeek: 1, totalUsage: 1_000_000 },
    { hourOfDay: 3, dayOfWeek: 2, totalUsage: 2_000_000 },
    { hourOfDay: 5, dayOfWeek: 3, totalUsage: 1_500_000 },
    { hourOfDay: 6, dayOfWeek: 4, totalUsage: 2_500_000 },
  ];

  const raw = isDemoOrLead ? staticDemoData : data_volume_per_hour || [];

  // Build a quick lookup: key = `${dow}-${hour}` -> totalUsage
  const valueByKey = useMemo(() => {
    const m = new Map();
    for (const d of raw) {
      m.set(`${d.dayOfWeek}-${d.hourOfDay}`, d.totalUsage || 0);
    }
    return m;
  }, [raw]);

  // Prepare Apex series: one series per day, 8 cells per series (hours)
  const series = useMemo(() => {
    return dayOfWeekNames.map((dayName, idx) => {
      const dow = idx + 1; // 1..7
      const data = hourOfDayNames.map((label, hour) => {
        const v = valueByKey.get(`${dow}-${hour}`) || 0;
        return { x: label, y: v }; // Apex heatmap expects numeric y
      });
      return { name: dayName, data };
    });
  }, [dayOfWeekNames, hourOfDayNames, valueByKey]);

  // Compute min/max to build a nice color scale
  const { minV, maxV } = useMemo(() => {
    let min = Number.POSITIVE_INFINITY;
    let max = 0;
    for (const s of series) {
      for (const pt of s.data) {
        const v = pt.y || 0;
        if (v < min) min = v;
        if (v > max) max = v;
      }
    }
    if (!isFinite(min)) min = 0;
    return { minV: min, maxV: max };
  }, [series]);

  // Build 5 color buckets from min→max (light→dark); adjust to your palette
  const ranges = useMemo(() => {
    const steps = 5; // three color levels: low, medium, high
    const r = [];
    const span = maxV - minV || 1;
    const stops = Array.from({ length: steps }, (_, i) =>
      Math.round(minV + (span * i) / (steps - 1))
    );

    // Define colors for low → medium → high
    const colors = [
      "#22C55E", // Green (low volume)
      "#84CC16",
      "#FACC15", // Yellow (medium)
      "#F97316",
      "#EF4444", // Red (high)
    ];

    for (let i = 0; i < stops.length; i++) {
      const from = i === 0 ? minV : stops[i - 1] + 1;
      const to = i === stops.length - 1 ? maxV : stops[i];
      r.push({ from, to, color: colors[i] });
    }

    return r;
  }, [minV, maxV, whitelabel?.accentColor]);

  const options = useMemo(
    () => ({
      chart: {
        type: "heatmap",
        toolbar: { show: false },
        animations: { enabled: true },
      },
      plotOptions: {
        heatmap: {
          shadeIntensity: 0.8,
          radius: 4,
          useFillColorAsStroke: true,
          colorScale: {
            ranges, // our computed ranges
          },
        },
      },
      dataLabels: { enabled: false },
      xaxis: {
        type: "category",
        categories: hourOfDayNames,
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: {
          rotate: -15,
          trim: true,
        },
        title: { text: "Hour of Day" },
      },
      yaxis: {
        // Apex uses series order as Y categories; Monday is first → appears on top
        labels: { style: { fontSize: "12px" } },
        title: { text: "Day of the Week" },
      },
      tooltip: {
        y: {
          formatter: (val) => `${(val / 1_000_000).toFixed(2)} MB`,
        },
        custom: ({ series, seriesIndex, dataPointIndex, w }) => {
          const value = series[seriesIndex][dataPointIndex] || 0;
          const day = w.globals.seriesNames[seriesIndex];
          const hour = w.globals.labels[dataPointIndex];
          return `<div style="padding:6px 8px;font-size:12px">
                    <strong>${day}</strong><br/>
                    ${hour}: ${(value / 1_000_000).toFixed(2)} MB
                  </div>`;
        },
      },
      grid: {
        padding: { top: 10, right: 10, bottom: 10, left: 10 },
        strokeDashArray: 0,
      },
      theme: {
        mode: "light",
      },
      legend: { show: false },
    }),
    // deps:
    [ranges, hourOfDayNames, whitelabel?.backgroundColor]
  );

  return (
    <Card className="flex-fill w-100" style={{ minHeight: "450px" }}>
      <Card.Header>
        <h4 className="card-title mb-0">{t("dashboard.dataVolumePerHour")}</h4>
      </Card.Header>
      <Card.Body>
        {!isDemoOrLead && !isLoading && data_volume_per_hour.length === 0 && (
          <div className="d-flex justify-content-center align-items-center">
            {t("No data available")}
          </div>
        )}

        {(isDemoOrLead || (!isLoading && data_volume_per_hour.length > 0)) && (
          <div style={{ height: "400px", width: "100%" }}>
            <Chart
              type="heatmap"
              options={options}
              series={series}
              height="100%"
              width="100%"
            />
          </div>
        )}

        {isLoading && (
          <div className="text-center">
            <Loader animation="border" variant="primary" />
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default DataVolumePerHourChart;
